import {
  type ShortCode,
  type EnrichmentContext,
  type TransformedItemPost,
  fetchEmbeddedPosts,
  fetchEmbeddedPostsByCat,
  withDefaultContributors,
} from '@highereducation/wordpress';
import {
  ArticleLayout,
  type ArticleLayoutProps,
} from '@highereducation/hen-ui';
import { parseTruthy, toArticleCardProps, parseShowImage } from '../utils';

interface ArticleLayoutShortcodeProps {
  /** Section title rendered as an <h2> above the layout grid. */
  title?: string;
  /** Comma-separated post IDs. Priority: ids > category > latest. */
  ids?: string;
  /** Comma-separated category slugs (AND logic: posts must match ALL). */
  category?: string;
  /** When truthy, fetches the most recent posts with no category filter. */
  latest?: string;
  /** Sort order for category/latest fetches. */
  order?: 'updated' | 'published' | 'custom';
  /** Max total articles to fetch (1 hero + rest vertical). Default: '6'. */
  limit?: string;
  /** Border variant for right-column cards. Default: 'none'. */
  border?: ArticleLayoutProps['border'];
  /** Hover style for right-column cards. Default: 'shadow'. */
  'hover-style'?: ArticleLayoutProps['hoverStyle'];
  /**
   * Set to 'false' to hide thumbnail images on right-column cards (text-only).
   * Default: 'true'.
   */
  'row-image'?: string;
  /**
   * Single post ID to pin as the featured left-column card.
   * When provided, this post is fetched independently and the right column
   * is populated separately via ids/category/latest. limit then applies
   * only to the right-column articles.
   * When omitted, all articles (hero + row) are fetched in a single batch.
   */
  'featured-card'?: string;
  /** URL for a CTA link below the right column. Suppresses pagination arrows. */
  link?: string;
  /** Display text for the CTA link. */
  'link-label'?: string;
  /**
   * Set to 'false' to hide category brow text on all articles.
   * Default: 'true' (show brow).
   */
  'show-brow'?: string | boolean;
  [key: string]: unknown;
}

interface ArticleLayoutEnrichment {
  featured?: TransformedItemPost;
  articles: TransformedItemPost[];
}

/**
 * ArticleLayoutShortcode — two-column section: featured overlay card (left) + vertical list (right).
 *
 * Fetches articles from the CMS and splits them: the first article becomes the
 * featured overlay card; the remaining articles populate the vertical list.
 *
 * Supported shortcode attributes:
 * - title: string        — Section title rendered as an h2 above the layout grid
 * - ids: string          — Comma-separated post IDs
 * - category: string     — Comma-separated category slugs (AND logic)
 * - latest: string       — Truthy ('true') to fetch most-recent posts
 * - order: string        — 'updated' | 'published' | 'custom' (default: 'updated')
 * - limit: string        — Total articles to fetch including hero (default: '6')
 * - border: string       — Right-column card border (default: 'none')
 * - hover-style: string  — Right-column card hover style (default: 'shadow')
 * - row-image: string    — 'false' to hide thumbnails in the right column (default: 'true')
 * - show-brow: string    — 'false' to hide category brow text on all articles (default: 'true')
 * - featured-card: string — Single post ID for the left column; row articles
 *     fetched separately via ids/category/latest; limit applies to row only
 * - link: string         — URL for a CTA link below the right column; suppresses pagination arrows
 * - link-label: string   — Display text for the CTA link
 *
 * @example Dynamic by category:
 * [hen-ui-article-layout category="ranking" limit="6"][/hen-ui-article-layout]
 *
 * @example Dynamic latest posts:
 * [hen-ui-article-layout latest="true" limit="6" order="published"][/hen-ui-article-layout]
 *
 * @example Static by IDs:
 * [hen-ui-article-layout ids="123,345,678,789" limit="6"][/hen-ui-article-layout]
 *
 * @example Hide images in the right column:
 * [hen-ui-article-layout category="ranking" limit="6" row-image="false"][/hen-ui-article-layout]
 *
 * @example Hide category brow on all articles:
 * [hen-ui-article-layout category="ranking" limit="6" show-brow="false"][/hen-ui-article-layout]
 *
 * @example Pin featured card, row from category:
 * [hen-ui-article-layout featured-card="123" category="ranking" limit="5"][/hen-ui-article-layout]
 *
 * @example Link instead of pagination arrows:
 * [hen-ui-article-layout category="ranking" limit="6" link="/articles" link-label="View all articles"][/hen-ui-article-layout]
 */
export const ArticleLayoutShortcode: ShortCode<
  ArticleLayoutShortcodeProps,
  ArticleLayoutEnrichment
> = {
  getEnrichedData: async (
    _content: string,
    context?: EnrichmentContext,
  ): Promise<ArticleLayoutEnrichment> => {
    const attrs = context?.attrs ?? {};
    const config = context?.config;
    if (!config?.cmsUrl || !config?.postTypeGql) return { articles: [] };

    const { cmsUrl, postTypeGql } = config;
    const featuredCardId = (
      attrs['featured-card'] as string | undefined
    )?.trim();
    const ids = attrs['ids'] as string | undefined;
    const category = attrs['category'] as string | undefined;
    const latest = attrs['latest'] as string | undefined;
    const order = attrs['order'] as
      | 'updated'
      | 'published'
      | 'custom'
      | undefined;
    const limit = (attrs['limit'] as string | undefined) ?? '6';
    const filters = { limit, orderBy: order };

    const fetchRowArticles = async (): Promise<TransformedItemPost[]> => {
      if (ids) {
        const idList = ids
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
        if (!idList.length) return [];
        const result = await fetchEmbeddedPosts(idList, config);
        if (!result) return [];
        return idList
          .map((id) => result[id])
          .filter((p): p is TransformedItemPost => p != null);
      }

      const parsedLatest = parseTruthy(latest);
      if (!category && !parsedLatest) return [];

      const categories = category
        ? category
            .split(',')
            .map((c) => c.trim())
            .filter(Boolean)
        : [];

      const result = await fetchEmbeddedPostsByCat(
        categories,
        filters,
        postTypeGql,
        cmsUrl,
        ...(config.fallbackPostImage ? [config.fallbackPostImage] : []),
      );
      return result ?? [];
    };

    if (featuredCardId) {
      const [featuredResult, rowArticles] = await Promise.all([
        fetchEmbeddedPosts([featuredCardId], config),
        fetchRowArticles(),
      ]);
      const featured = featuredResult?.[featuredCardId] ?? undefined;
      const toEnrich = featured ? [featured, ...rowArticles] : rowArticles;
      const enriched = await withDefaultContributors(toEnrich, config);
      if (featured) {
        return { featured: enriched[0], articles: enriched.slice(1) };
      }
      return { featured: undefined, articles: enriched };
    }

    const posts = await fetchRowArticles();
    return {
      featured: undefined,
      articles: await withDefaultContributors(posts, config),
    };
  },

  component: ({
    title,
    ids,
    category,
    latest,
    'featured-card': featuredCardAttr,
    border = 'padding',
    'hover-style': hoverStyle = 'shadow',
    'row-image': rowImageAttr,
    'show-brow': showBrowAttr,
    link,
    'link-label': linkLabel,
    enrichments,
  }) => {
    const showRowImages = parseShowImage(rowImageAttr);
    const showBrow = parseShowImage(showBrowAttr);
    const { featured, articles } = (enrichments?.data as
      | ArticleLayoutEnrichment
      | null
      | undefined) ?? { articles: [] };

    const hasIds = Boolean(
      ids
        ?.split(',')
        .map((s) => s.trim())
        .filter(Boolean).length,
    );
    const hasCategory = Boolean(
      category
        ?.split(',')
        .map((s) => s.trim())
        .filter(Boolean).length,
    );
    const parsedLatest = parseTruthy(latest);
    const hasDynamicAttrs =
      hasIds ||
      hasCategory ||
      parsedLatest ||
      Boolean(featuredCardAttr?.trim());

    if (!hasDynamicAttrs || (!featured && !articles.length)) {
      return (
        <ArticleLayout
          title={title}
          border={border}
          hoverStyle={hoverStyle}
          showRowImages={showRowImages}
          link={link}
          linkLabel={linkLabel}
        />
      );
    }

    const featuredPost = featured ?? articles[0];
    const rowPosts = featured ? articles : articles.slice(1);

    return (
      <ArticleLayout
        title={title}
        featuredArticle={
          featuredPost
            ? {
                ...toArticleCardProps(featuredPost),
                ...(showBrow ? {} : { brow: undefined }),
              }
            : undefined
        }
        articles={rowPosts.map((p) => ({
          ...toArticleCardProps(p),
          ...(showBrow ? {} : { brow: undefined }),
        }))}
        border={border}
        hoverStyle={hoverStyle}
        showRowImages={showRowImages}
        link={link}
        linkLabel={linkLabel}
      />
    );
  },
};
